import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FlaskConical, UserPlus } from "lucide-react";
import { stockLabel, getVariant, hasMultipleVariants, minPrice, productInStock, resolveImage } from "../data/products";
import { useAuth } from "../context/AuthContext";
import ProductImage from "./ProductImage";
import { SalePrice, SaleBadge } from "./SalePrice";

const toneClass = {
  ok:  "text-sage-600 bg-white/85",
  low: "text-amber-700 bg-white/85",
  out: "text-charcoal-400 bg-white/85",
};

// Products with larger-format bottles (amber jars, etc.) — render at natural framing.
// All other "standard vials" get a slight zoom + lower-center crop so they appear uniform
// in the catalog grid (the bottles in those source photos are smaller relative to the frame).
const NATURAL_FRAMING_SLUGS = new Set(["nad-plus", "glutathione", "b12"]);
// Photos where the vial fills LESS of the source frame than our standard photos —
// nudge these up with a small transform-scale so the vial reads at the same visual
// size as its siblings in the grid. Kept as `object-cover` so no letterboxing.
const ZOOM_IN_SLUGS = new Set(["cagrilintide"]);

export const ProductCard = ({ product, index = 0 }) => {
  const { isAuthenticated } = useAuth();
  // For card display, show the default variant's stock state (or "out" if all are out).
  const defaultVariant = getVariant(product, product.defaultVariant);
  const isAnyInStock = productInStock(product);
  const stock = isAnyInStock
    ? stockLabel(defaultVariant)
    : { label: "Out of stock", tone: "out" };
  const isOut = stock.tone === "out";
  const multi = hasMultipleVariants(product);
  const lowest = minPrice(product);
  const { src: cardImageSrc, fallback: cardImageFallback } = resolveImage(product, defaultVariant);

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group block animate-fade-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="surface-card overflow-hidden hover:shadow-hover transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
        <div className="relative aspect-[4/5] bg-cream-200 overflow-hidden">
          <ProductImage
            src={cardImageSrc}
            fallback={cardImageFallback}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${
              isOut ? "grayscale opacity-80" : ""
            } ${NATURAL_FRAMING_SLUGS.has(product.slug) ? "" : "[object-position:center_42%]"} ${
              ZOOM_IN_SLUGS.has(product.slug) ? "scale-[1.15]" : ""
            }`}
            loading="lazy"
          />
          <span className="absolute top-4 left-4 badge-compliance bg-white/85 backdrop-blur">
            Research Use Only
          </span>
          <SaleBadge slug={product.slug} className="absolute top-12 left-4" />
          <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-medium text-charcoal-500 bg-white/85 backdrop-blur px-2.5 py-1 rounded-full uppercase tracking-[0.15em]">
            <FlaskConical className="w-3 h-3" strokeWidth={2} />
            {product.purity}
          </span>
          <span
            data-testid={`product-stock-${product.slug}`}
            className={`absolute bottom-4 left-4 inline-flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-[0.15em] backdrop-blur ${toneClass[stock.tone]}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${
              stock.tone === "ok"  ? "bg-sage-500" :
              stock.tone === "low" ? "bg-amber-500" :
                                     "bg-charcoal-300"
            }`} />
            {stock.label}
          </span>
          {multi && (
            <span className="absolute bottom-4 right-4 inline-flex items-center text-[10px] font-medium text-charcoal-600 bg-white/85 backdrop-blur px-2.5 py-1 rounded-full uppercase tracking-[0.15em]">
              {product.variants.length} sizes
            </span>
          )}
        </div>

        <div className="p-6 flex-1 flex flex-col">
          <p className="overline mb-2">{defaultVariant.label}</p>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <h3 className="font-display text-2xl font-bold leading-none text-charcoal-600">
              {product.name}
            </h3>
            <SalePrice
              slug={product.slug}
              price={multi ? lowest : defaultVariant.price}
              prefix={multi ? "From " : ""}
              testId={`product-price-${product.slug}`}
              className="font-display text-lg font-bold text-charcoal-600 whitespace-nowrap"
            />
          </div>
          <p className="text-sm text-charcoal-400 leading-relaxed line-clamp-2 mb-5">
            {product.subtitle}
          </p>
          <div className="mt-auto flex items-center justify-between text-sm">
            {isAuthenticated ? (
              <>
                <span
                  data-testid={`product-card-cta-authed-${product.slug}`}
                  className="text-sage-600 font-medium group-hover:translate-x-0.5 transition-transform"
                >
                  View research details
                </span>
                <ArrowUpRight
                  className="w-4 h-4 text-sage-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  strokeWidth={1.8}
                />
              </>
            ) : (
              <>
                <span
                  data-testid={`product-card-cta-guest-${product.slug}`}
                  className="inline-flex items-center gap-1.5 text-[#8B2252] font-medium group-hover:translate-x-0.5 transition-transform"
                >
                  <UserPlus className="w-4 h-4" strokeWidth={1.8} />
                  Create Account to Purchase
                </span>
                <ArrowUpRight
                  className="w-4 h-4 text-[#8B2252] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  strokeWidth={1.8}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
